import { toLocalDateInputValue, isDateInRange } from '@/lib/dates';

/**
 * Which calendar days the Concrete Pour Schedule shows.
 *
 * Pulled out of the grid so the rules below are assertable — the component
 * itself can't be driven in a test (apps/web runs vitest in a node environment
 * with no DOM), and these are the parts that would break silently.
 */

export type RangePreset = 'upcoming' | 'all';

export interface PourRange {
  preset: RangePreset;
  /** Inclusive YYYY-MM-DD bounds. Either may be '' for an open side. */
  from: string;
  to: string;
}

/** True once the reader has set a bound, which is what suppresses the preset. */
export function hasBounds(range: PourRange): boolean {
  return range.from !== '' || range.to !== '';
}

/**
 * Days at or after this are "upcoming". A week of hindsight, because a pour
 * poured on Monday is still what you're reconciling on Wednesday.
 *
 * Built from LOCAL components, never `toISOString()`. The day keys this is
 * compared against are wall-clock dates, but the boundary is a real-world
 * "7 days ago from where the reader is standing" — and the UTC date of that
 * instant runs a day ahead all evening in Eastern, which silently dropped the
 * oldest day of pours out of the view after 8pm.
 */
export function lookbackCutoff(now: Date, days = 7): string {
  const d = new Date(now);
  d.setDate(d.getDate() - days);
  return toLocalDateInputValue(d);
}

/** Days back the one-click long view reaches. See LOOKBACK_DAYS below. */
export const LOOKBACK_DAYS = 90;

/**
 * The one-click long view: `from` that far back, `to` left open so upcoming
 * pours stay on screen. In other words Upcoming, with a season of hindsight
 * instead of a week.
 *
 * ⚠️ It reaches BACKWARD on purpose. A pour card is born when the field
 * submits a Pre-Pour Checklist, which happens days before the pour — not
 * months — so nothing on this grid is ever scheduled a quarter out. A
 * today→+90 window shipped in #336 and could only ever show the same handful
 * of days Upcoming already did (9 of 93 pours on the day it went live, none
 * dated beyond six days out). Everything the reader might want a longer view
 * OF is behind them.
 */
export function lookbackWindow(now: Date, days = LOOKBACK_DAYS): { from: string; to: string } {
  return { from: lookbackCutoff(now, days), to: '' };
}

/**
 * Is this wall-clock day (YYYY-MM-DD) in view?
 *
 * ⚠️ Explicit bounds REPLACE the preset — they do not narrow it. Intersecting
 * the two would silently ignore any `from` earlier than Upcoming's own cutoff,
 * so asking for last quarter while Upcoming happened to be selected would
 * return nothing and read as a broken filter. Clearing both bounds therefore
 * falls back to the preset with no third "custom but empty" state to hold.
 */
export function includesDay(range: PourRange, cutoff: string, ymd: string): boolean {
  if (hasBounds(range)) return isDateInRange(ymd, range.from, range.to);
  return range.preset === 'all' || ymd >= cutoff;
}

/**
 * Which day column the schedule should open scrolled to: the first pour day on
 * or after today (PE ask, 2026-09-24: "today at the very left as soon as you
 * open that tab"). The columns are only days WITH pours, so today often has no
 * column of its own — the next pour day stands in for it. When every column is
 * in the past, the last one: the most recent pours are the relevant ones.
 *
 * `days` must be sorted YYYY-MM-DD keys, as the grid builds them. Returns -1
 * for an empty grid.
 */
export function openingDayIndex(days: readonly string[], todayKey: string): number {
  if (days.length === 0) return -1;
  const i = days.findIndex((d) => d >= todayKey);
  return i === -1 ? days.length - 1 : i;
}
